import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';


const EligibilityForm = ({ formData, errors, onChange }) => {
  const [newSkill, setNewSkill] = useState('');
  const [skillProficiency, setSkillProficiency] = useState('intermediate');
  const [newLocation, setNewLocation] = useState('');

  const educationLevelOptions = [
    { value: 'high-school', label: 'High School' },
    { value: 'diploma', label: 'Diploma' },
    { value: 'bachelors', label: "Bachelor\'s Degree" },
    { value: 'masters', label: "Master\'s Degree" },
    { value: 'phd', label: 'PhD' }
  ];

  const proficiencyOptions = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'expert', label: 'Expert' }
  ];

  const handleAddSkill = () => {
    if (newSkill?.trim()) {
      const skillWithProficiency = `${newSkill} (${skillProficiency})`;
      onChange('requiredSkills', [...(formData?.requiredSkills || []), skillWithProficiency]);
      setNewSkill('');
      setSkillProficiency('intermediate');
    }
  };

  const handleRemoveSkill = (index) => {
    const updated = formData?.requiredSkills?.filter((_, i) => i !== index);
    onChange('requiredSkills', updated);
  };

  const handleAddLocation = () => {
    if (newLocation?.trim()) {
      onChange('preferredLocations', [...(formData?.preferredLocations || []), newLocation]);
      setNewLocation('');
    }
  };

  const handleRemoveLocation = (index) => {
    const updated = formData?.preferredLocations?.filter((_, i) => i !== index);
    onChange('preferredLocations', updated);
  };

  return (
    <div className="space-y-6">
      <div>
        <Select
          label="Educational Qualification"
          description="Select all acceptable education levels"
          options={educationLevelOptions}
          value={formData?.educationLevel}
          onChange={(value) => onChange('educationLevel', value)}
          multiple
          searchable
          error={errors?.educationLevel}
          required
        />
      </div>

      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">
          Required Skills <span className="text-destructive">*</span>
        </label>
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Enter skill name (e.g., React.js, Python)"
                value={newSkill}
                onChange={(e) => setNewSkill(e?.target?.value)}
                onKeyPress={(e) => {
                  if (e?.key === 'Enter') {
                    e?.preventDefault();
                    handleAddSkill();
                  }
                }}
              />
            </div>
            <div className="w-40">
              <Select
                options={proficiencyOptions}
                value={skillProficiency}
                onChange={(value) => setSkillProficiency(value)}
                placeholder="Level"
              />
            </div>
            <Button
              onClick={handleAddSkill}
              iconName="Plus"
              iconPosition="left"
            >
              Add
            </Button>
          </div>
          {errors?.requiredSkills && (
            <p className="text-sm text-destructive">{errors?.requiredSkills}</p>
          )}
          {formData?.requiredSkills?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData?.requiredSkills?.map((skill, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm"
                >
                  <span>{skill}</span>
                  <button
                    onClick={() => handleRemoveSkill(index)}
                    className="hover:bg-primary/20 rounded-full p-0.5 transition-smooth"
                  >
                    <Icon name="X" size={14} color="currentColor" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">
          Experience Required (Years)
        </label>
        <div className="grid grid-cols-2 gap-4">
          <Input
            type="number"
            placeholder="Min (e.g., 0)"
            value={formData?.experienceMin}
            onChange={(e) => onChange('experienceMin', e?.target?.value)}
          />
          <Input
            type="number"
            placeholder="Max (e.g., 3)"
            value={formData?.experienceMax}
            onChange={(e) => onChange('experienceMax', e?.target?.value)}
          />
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Leave blank for fresher positions or if experience is flexible
        </p>
      </div>

      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">
          Preferred Candidate Locations
        </label>
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Enter city or state (e.g., Mumbai, Maharashtra)"
                value={newLocation}
                onChange={(e) => setNewLocation(e?.target?.value)}
                onKeyPress={(e) => {
                  if (e?.key === 'Enter') {
                    e?.preventDefault();
                    handleAddLocation();
                  }
                }}
              />
            </div>
            <Button
              onClick={handleAddLocation}
              iconName="Plus"
              iconPosition="left"
            >
              Add
            </Button>
          </div>
          {formData?.preferredLocations?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData?.preferredLocations?.map((location, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 px-3 py-1.5 bg-secondary/10 text-secondary rounded-full text-sm"
                >
                  <Icon name="MapPin" size={14} color="currentColor" />
                  <span>{location}</span>
                  <button
                    onClick={() => handleRemoveLocation(index)}
                    className="hover:bg-secondary/20 rounded-full p-0.5 transition-smooth"
                  >
                    <Icon name="X" size={14} color="currentColor" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="text-sm text-muted-foreground">
            Specify preferred locations for candidates. Leave empty if location doesn't matter.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EligibilityForm;